import type { ButtonColor } from '../../components/Button/types';
import { ColorTokens, colorTokens, ScaleTokens, scaleTokens } from './semantic';

// Generic toolbar buttons: Filter, the days-back picker and CSV export. Quick
// filters are secondary.
const toolbar: { buttonColor: ButtonColor } = { buttonColor: 'primary' };

export const buildComponentTokens = (
  {
    content,
    surface,
    border,
    icon,
    status,
    accent,
    shadow: shadowColor,
  }: ColorTokens,
  {
    space,
    radius,
    fontSize,
    fontWeight,
    lineHeight,
    fontFamily,
    shadow,
  }: ScaleTokens,
) => ({
  globalStyle: {
    fontFamily: fontFamily.sansFace,
    codeFontFamily: fontFamily.mono,
    fontFeatureSettings:
      "'ss01' on, 'ss03' on, 'ss05' on, 'liga' off, 'clig' off",
    // Loaded on demand; the Elementary fonts are linked in each app's HTML.
    fontStylesheet: '',
    pageBackground: 'transparent',
    textColor: 'inherit',
    scrollbarThumb: surface.scrollbar,
    // Keyboard focus ring; without a color, controls keep the browser's.
    focusRing: '',
    honorReducedMotion: false,
  },
  typography: {
    color: content.primary,
    fontSize: fontSize.base,
    displayHeadingWeight: fontWeight.light,
    headingWeight: fontWeight.bold,
    smallHeadingWeight: fontWeight.bold,
    headingSize: {
      h3: fontSize.xl,
      h4: fontSize.displayLg,
      h5: fontSize.xxl,
      h6: fontSize.lg,
    },
    body1: {
      color: content.secondary,
      fontSize: fontSize.base,
      fontWeight: fontWeight.regular,
    },
    caption: {
      color: content.placeholder,
      fontSize: fontSize.base,
      lineHeight: lineHeight.relaxed,
    },
  },
  formControl: {
    label: content.placeholder,
    labelFocused: content.primary,
    labelFontSize: fontSize.base,
    labelPaddingRight: space.xs,
    labelPaddingTop: space.hairline,
  },
  inputLabel: {
    color: content.primary,
  },
  formControls: {
    errorText: {
      fontSize: fontSize.xxs,
      lineHeight: '10px',
    },
  },
  formField: {
    errorText: {
      fontSize: fontSize.xxs,
      lineHeight: '10px',
    },
  },
  input: {
    radius: radius.lg,
    outlineBorder: border.translucentStrong,
  },
  // MUI's base corner, used by MUI components that set none of their own.
  muiShape: {
    borderRadius: 4,
  },
  spinner: {
    color: accent.primary,
  },
  link: {
    color: accent.primary,
    hoverColor: '',
    underlineOnlyOnHover: false,
    labelColor: content.primary,
    labelHoverColor: accent.primary,
    breadcrumbColor: content.disabled,
    // The breadcrumb tree toggle; without a color it keeps its icon colors.
    treeToggleIcon: '',
  },
  menu: {
    border: border.light,
    radius: radius.lg,
    shadow: shadow.md,
    item: {
      padding: `${space.smMinus} ${space.lg}`,
      paddingSm: space.sm,
      paddingLg: space.lg,
      borderRadius: radius.lg,
      hoverBackground: surface.tinted,
      selectedBackground: surface.sunken,
      selectedHoverBackground: surface.sunken,
      // Selected items in menus that don't highlight the selection.
      plainSelectedBackground: 'none',
      fontWeight: fontWeight.regular,
    },
    title: {
      fontSize: fontSize.sm,
      color: content.disabled,
      padding: `${space.sm} ${space.lg}`,
    },
    optionDescription: {
      fontSize: fontSize.sm,
      color: content.tertiary,
    },
    clearItem: {
      margin: `0 ${space.xs} ${space.sm}`,
      fontSize: fontSize.base,
      color: accent.link,
    },
    options: {
      gap: space.sm,
      margin: `0 ${space.xs}`,
    },
  },
  autocomplete: {
    border: border.subtle,
    radius: radius.md,
    shadow: shadow.md,
    popupIndicator: {
      color: icon.translucent.active,
      hoverBackground: surface.translucent.hover,
      disabledColor: icon.translucent.disabled,
    },
    textField: {
      endAdornmentMarginLeft: space.xs,
      disabledBackground: surface.muted,
    },
    wrapper: {
      disabledBackground: surface.muted,
      inputPadding: space.xs,
    },
    tagChip: {
      color: content.primary,
    },
    editInput: {
      fontSize: fontSize.base,
      padding: `0 ${space.xs}`,
      color: content.primary,
    },
    suggestionList: {
      padding: space.xs,
    },
    suggestionItem: {
      fontSize: fontSize.base,
      padding: `${space.mdMinus} ${space.lg}`,
      borderRadius: radius.lg,
      backgroundColor: surface.translucent.hover,
      hoverBackgroundColor: surface.translucent.hover,
    },
    noOptions: {
      fontSize: fontSize.base,
      padding: `${space.mdPlus} ${space.lg}`,
      color: content.translucent.secondary,
    },
    actionItem: {
      fontSize: fontSize.base,
      fontWeight: fontWeight.semibold,
      color: accent.link,
      padding: `${space.mdMinus} ${space.lg}`,
      borderRadius: radius.lg,
      background: surface.base,
      hoverBackgroundColor: surface.tinted,
    },
    invalidChipWrapper: {
      textDecorationColor: status.error.main,
      padding: `0 ${space.sm}`,
    },
    paper: {
      padding: space.xs,
      borderBottom: border.subtle,
      margin: `${space.xs} 0`,
    },
    renderOption: {
      color: content.tertiary,
    },
    listboxTitle: {
      padding: `${space.smMinus} ${space.lg}`,
      fontSize: fontSize.sm,
      fontWeight: fontWeight.medium,
      color: content.tertiary,
    },
    listbox: {
      padding: space.xs,
    },
    option: {
      fontSize: fontSize.base,
      padding: `${space.mdMinus} ${space.lg}`,
      borderRadius: radius.lg,
      background: surface.base,
      hoverBackground: surface.tinted,
    },
    groupLabel: {
      fontSize: fontSize.sm,
      fontWeight: fontWeight.semibold,
      color: content.disabled,
      padding: `${space.sm} ${space.lg} ${space.xs}`,
    },
  },
  dialog: {
    border: border.subtle,
    radius: radius.lg,
    backdrop: surface.backdrop,
    title: {
      gap: space.sm,
      padding: `${space.md} ${space.lg}`,
      backgroundColor: surface.sunken,
      fontSize: fontSize.base,
      fontWeight: fontWeight.semibold,
      lineHeight: lineHeight.relaxed,
    },
    message: {
      fontSize: fontSize.sm,
      color: content.tertiary,
    },
    content: {
      gap: space.lg,
      padding: `${space.xl} ${space.xxl}`,
    },
    buttonContainer: {
      gap: space.lg,
      lineHeight: '42px',
    },
    footer: {
      gap: space.lg,
      borderTop: border.subtle,
      padding: `${space.lg} ${space.xxl}`,
    },
    button: {
      marginLeft: '30px',
    },
  },
  drawer: {
    footerHeight: '72px',
    title: {
      fontSize: fontSize.lg,
      fontWeight: fontWeight.semibold,
    },
    paper: {
      shadow: shadow.md,
      radius: `${radius.lg} 0 0 ${radius.lg}`,
    },
    wrapper: {
      backgroundColor: surface.base,
      borderRadius: `${radius.lg} 0 0 ${radius.lg}`,
      border: border.divider,
    },
    header: {
      padding: `${space.lg} ${space.xxl}`,
      borderBottom: border.divider,
    },
    footer: {
      padding: `${space.lg} ${space.xxl}`,
      borderTop: border.divider,
    },
    content: {
      padding: space.xxl,
    },
    closeIcon: {
      fill: icon.primary,
    },
    headerLeft: {
      gap: space.lg,
    },
    headerRight: {
      gap: space.md,
    },
    backArrowButton: {
      fill: icon.secondary,
      disabledFill: icon.primary,
    },
  },
  divider: {
    color: border.divider,
    lightColor: border.subtle,
  },
  tooltip: {
    bg: surface.inverse,
    color: content.inverse,
    radius: radius.lg,
    padding: space.sm,
    fontSize: fontSize.sm,
    fontWeight: fontWeight.regular,
    maxWidth: '400px',
    informationIcon: {
      pathFill: icon.disabled,
      hoverPathFill: icon.primary,
    },
  },
  alert: {
    radius: radius.lg,
    fg: content.primary,
    successBg: status.success.bg,
    errorBg: status.error.bg,
    warningBg: status.warning.bg,
    attentionBg: status.attention.bg,
    infoBg: status.info.bg,
    iconSize: '32px',
    standardInfo: {
      background: status.info.bgStrong,
      iconFill: accent.secondary,
    },
    // Alerts drawn as inline alerts: text on `background`, with the border and
    // icon in each severity's color. Without a background they keep the pale
    // severity fills.
    inline: {
      background: '',
      text: '',
      success: '',
      info: '',
      warning: '',
      error: '',
    },
    title: {
      marginBottom: space.xs,
    },
    // Full-width page alerts drawn as a banner with only a bottom border,
    // in the severity's color; without a width they keep the alert's border.
    page: {
      bottomBorder: '',
    },
    // The close button's color; without one it takes the alert's text color.
    closeIcon: { color: '' },
  },
  accordion: {
    summaryDivider: border.dividerSubtle,
  },
  card: {
    radius: radius.xl,
  },
  // Line illustrations on integration cards; with a tile color they are
  // redrawn in `line` on that tile.
  illustration: {
    tile: '',
    line: '',
    radius: radius.md,
  },
  codeEditor: {
    // The Ace theme; a look without one draws the editor in `colors`.
    aceTheme: 'monokai',
    radius: radius.lg,
    colors: {
      background: '',
      gutter: '',
      gutterText: '',
      border: '',
      disabledBorder: '',
      disabledGutterText: '',
      indentGuide: '',
      activeLine: '',
      activeGutter: '',
      selection: '',
      text: '',
      secondaryText: '',
      keyword: '',
      keywordWeight: fontWeight.semibold,
      operatorWeight: fontWeight.regular,
      function: '',
      string: '',
      number: '',
    },
    toolbar: {
      background: surface.translucent.scrim,
      border: 'none',
      disabledBorder: 'none',
      radius: radius.lg,
      // Empty keeps MUI's round icon buttons and hover.
      buttonRadius: '',
      buttonHoverBackground: '',
    },
    chat: {
      background: surface.inverse,
      border: 'none',
      radius: radius.lg,
      bar: surface.inverse,
      barDivider: 'none',
      meta: content.tertiary,
      link: accent.link,
      editorBorder: '',
      editorRadius: '',
    },
  },
  emptyState: {
    // With an icon color, the empty state draws an icon in place of its
    // illustration.
    icon: '',
    title: {
      fontSize: fontSize.lg,
      fontWeight: fontWeight.semibold,
      lineHeight: lineHeight.relaxed,
    },
    message: {
      color: content.secondary,
      fontSize: fontSize.lg,
      lineHeight: lineHeight.relaxed,
    },
    // Empty states inside small widgets.
    compactMessage: {
      fontSize: fontSize.xxs,
    },
    pageTitle: {
      fontSize: fontSize.xxl,
      fontWeight: fontWeight.semibold,
    },
    // Draws the no-environments illustration made for dark pages.
    darkIntegrationsGraphic: false,
  },
  aiChat: {
    askGradient: {
      start: border.highlight,
      end: accent.secondary,
    },
    submitHoverBg: surface.hover,
    // Without a pressed color the model pill keeps the button's.
    modelPillPressedBg: '',
    messageBorder: border.highlight,
    lineageToolConnector: border.control,
    tableToolFade: surface.fade,
    toolShimmer: surface.translucent.frostedLight,
    // Suggested prompts; without a text color they keep the outlined button.
    suggestion: {
      bg: '',
      border: '',
      fg: '',
      hoverBg: '',
    },
    envChip: {
      // Without a label color the label keeps the text color.
      label: '',
      color: content.tertiary,
      icon: icon.tertiary,
      hoverBg: surface.muted,
      pressedBg: '',
    },
  },
  // Ask AI and the other AI action buttons. With the gradient border off they
  // are drawn as a borderless button on these backgrounds.
  aiButton: {
    gradientBorder: true,
    bg: '',
    hoverBg: '',
    pressedBg: '',
  },
  avatar: {
    extraSmallFontSize: '8px',
  },
  fillBar: {
    trackStroke: surface.sunken,
  },
  floatingActionsMenu: {
    radius: '24px',
    bg: surface.translucent.frostedGray,
    border: 'none',
    dropShadow: `drop-shadow(0px 8px 24px ${shadowColor.medium})`,
    shadow: 'none',
    itemHoverBg: surface.translucent.subtle,
    placeholder: accent.tertiary,
  },
  incrementSelector: {
    border: border.tinted,
    // Icon of the minus and plus tiles; without one they take the tile icon.
    icon: '',
  },
  wizard: {
    selectedBackground: surface.tinted,
    hoverBackground: surface.muted,
    selectedHoverBackground: surface.muted,
    doneIndex: content.subtle,
  },
  // The app header.
  topBar: {
    background: surface.soft,
    productionBackground: surface.selectedSubtle,
    border: border.divider,
    logoBackground: accent.ai,
    // A divider between the logo and the navbar toggle, for a logo tile in
    // the bar's own color.
    logoDivider: false,
    hoverBackground: surface.translucent.pressed,
    // On a brand-colored bar, text, icons and the focus ring take these;
    // without them the header keeps its own colors.
    text: '',
    icon: '',
    // Disc behind the AI avatar and the icon drawn on it while the AI panel
    // is open; without them the avatar keeps its own colors.
    avatarBackground: '',
    avatarIcon: '',
    // The environment menu's production badge; without colors it keeps the
    // primary chip's.
    productionChip: { fg: '', bg: '' },
  },
  treeView: {
    // With a color, folders are drawn as solid icons in it.
    folderIcon: '',
    // Chevrons and other neutral icons of the tree; without a color they keep
    // their own.
    icon: '',
  },
  navbar: {
    background: surface.soft,
    border: border.divider,
    // Icons of the items; without colors they keep their own.
    icon: '',
    selectedIcon: '',
    community: {
      border: accent.ai,
      text: accent.ai,
      background: accent.aiFaint,
      icon: accent.ai,
      radius: radius.lg,
    },
    cloudTooltip: {
      shadow: shadow.md,
    },
    expandedShadow: `4px 0 16px -4px ${shadowColor.soft}, 8px 0 32px -8px ${shadowColor.light}`,
  },
  lineage: {
    minimapBorder: border.mediumStrong,
    greyedOutStroke: border.light,
    edge: icon.disabled,
    infoPanelGripBorder: border.mediumSoft,
    category: {
      source: { border: accent.secondaryMuted, background: surface.highlight },
      exposure: { border: accent.tertiary, background: accent.tertiarySubtle },
      semantic: {
        border: status.success.bright,
        background: status.success.bgBright,
      },
      failing: { border: status.error.soft, background: status.error.bg },
      warning: { border: status.warning.strong, background: status.warning.bg },
      passing: { border: status.success.main, background: status.success.bg },
      neutral: { border: border.medium, background: surface.base },
    },
    contextMenu: {
      border: border.faint,
      background: surface.muted,
    },
    // Rows of the node menu; with a text color they are drawn as menu items.
    nodeMenuItem: {
      text: '',
      icon: '',
      hoverBackground: '',
      radius: '',
    },
    // Icons of the zoom, fit-view and export buttons; without a color they
    // keep the browser's.
    controls: {
      icon: '',
      disabledIcon: '',
    },
    // The direction toggle; with a track color it is one segmented control.
    streamToggle: {
      track: '',
      segment: '',
      hoverSegment: '',
      selectedSegment: '',
      // Without icon colors the segments keep the inverse and primary icons.
      icon: '',
      selectedIcon: '',
      disabledIcon: '',
      radius: radius.lg,
    },
  },
  checkbox: {
    border: icon.subtle,
    uncheckedBg: surface.base,
    checkedBg: surface.inverse,
    mark: icon.inverse,
    // The unchecked box is inset by half its stroke.
    boxRadius: '5.375px',
    checkedBoxRadius: radius.md,
    disabledBorder: icon.subtle,
    disabledCheckedBg: surface.inverse,
    disabledMark: icon.inverse,
  },
  radio: {
    ring: icon.subtle,
    checked: icon.primary,
    disabledRing: icon.subtle,
    disabledChecked: icon.primary,
    disabledOpacity: 0.4,
  },
  daysBackPicker: {
    // Without a label color the label keeps the text color.
    label: '',
    chevron: icon.tertiary,
  },
  toolbar,
  button: {
    radius: radius.lg,
    disabledIcon: icon.disabled,
    loadingStartPadding: space.huge,
    size: {
      extraSmall: {
        height: '28px',
        padding: `${space.xs} ${space.sm}`,
        fontSize: fontSize.sm,
        fontWeight: fontWeight.medium,
      },
      medium: {
        height: '32px',
        padding: `${space.smMinus} ${space.mdMinus}`,
        fontSize: fontSize.base,
        fontWeight: fontWeight.medium,
      },
      large: {
        height: '40px',
        padding: `${space.mdMinus} ${space.lg}`,
        fontSize: fontSize.lg,
        fontWeight: fontWeight.semibold,
      },
    },
    // Neutral-only icons take the label color.
    labelColoredIcons: false,
    // Gray tile buttons (steppers, stream toggles); without an icon color they
    // keep their own icon colors.
    tile: {
      icon: '',
      disabledIcon: '',
    },
    // A quick filter that is on; without a background or label color it keeps
    // the outlined button's.
    quickFilterActive: {
      border: border.strong,
      bg: '',
      fg: '',
    },
    contained: {
      primary: {
        bg: accent.primary,
        fg: content.inverse,
        pressedBg: '',
        disabledBg: surface.hover,
        disabledFg: content.disabled,
        disabledIcon: icon.disabled,
      },
      secondary: {
        bg: surface.base,
        fg: content.primary,
        hoverBg: surface.hover,
        hoverFg: content.primary,
        pressedBg: '',
        disabledBg: surface.base,
        disabledFg: content.disabled,
        disabledIcon: icon.disabled,
      },
      error: {
        bg: status.error.main,
        fg: content.inverse,
        hoverBg: status.error.main,
        hoverFilter: 'brightness(110%)',
      },
    },
    outlined: {
      primary: {
        bg: surface.base,
        border: border.control,
        fg: content.primary,
        hoverBg: surface.hover,
        hoverBorder: border.control,
        hoverFg: content.primary,
        pressedBg: '',
        disabledBg: surface.muted,
        disabledFg: content.disabled,
        disabledBorder: '',
        disabledIcon: icon.disabled,
      },
      secondary: {
        bg: surface.subtle,
        border: border.control,
        fg: content.primary,
        hoverBg: surface.subtle,
        hoverBorder: border.strong,
        hoverFg: content.primary,
        pressedBg: '',
        disabledBg: '',
        disabledFg: '',
        disabledBorder: '',
        disabledIcon: '',
      },
    },
    text: {
      primary: {
        hoverBg: 'none',
        hoverFg: '',
        pressedBg: '',
      },
      secondary: {
        fg: content.primary,
        hoverBg: 'none',
        hoverFg: content.primary,
        pressedBg: '',
        disabledFg: '',
        disabledIcon: '',
      },
    },
  },
  chip: {
    radius: radius.sm,
    roundedRadius: radius.xxl,
    fontSize: fontSize.base,
    fontWeight: fontWeight.regular,
    gap: space.xs,
    padding: `${space.xs} ${space.smMinus}`,
    deletablePadding: `${space.xs} ${space.xs} ${space.smMinus} ${space.smMinus}`,
    roundedPadding: `${space.xs} ${space.mdMinus} ${space.smMinus} ${space.smMinus}`,
    roundedDeletablePadding: `${space.xs} ${space.smMinus} ${space.smMinus} ${space.smMinus}`,
    roundedLabelPaddingLeft: space.xxsPlus,
    deletableLabelPaddingRight: space.md,
    deleteIconHitAreaRadius: `0 ${radius.sm} ${radius.sm} 0`,
    deleteIconHoverBg: surface.translucent.hover,
    dashedBorder: border.medium,
    smallFontSize: fontSize.xs,
    mediumFontSize: fontSize.xs,
    // Without line heights chips take their container's.
    lineHeight: '',
    smallLineHeight: '',
    mediumLineHeight: '',
    colors: {
      error: { fg: status.error.bright, bg: status.error.bgStrong },
      warning: { fg: status.warning.main, bg: status.warning.bgStrong },
      success: { fg: status.success.bright, bg: status.success.bgBright },
      idle: { fg: accent.secondary, bg: surface.highlight },
      merged: { fg: accent.tertiary, bg: accent.tertiarySubtle },
      primary: { fg: accent.secondary, bg: surface.highlight },
      default: {
        fg: content.secondary,
        bg: surface.sunken,
        customVariantBg: surface.base,
      },
    },
    quickFilter: {
      padding: `${space.xs} ${space.md}`,
      marginRight: space.sm,
      color: content.primary,
      background: surface.base,
      hoverBackground: surface.sunken,
      selectedColor: content.inverse,
      selectedBackground: surface.inverse,
      selectedHoverBackground: surface.inverse,
    },
    editable: {
      backgroundColor: surface.base,
      borderRadius: radius.xs,
      border: border.default,
      clickableHoverBackgroundColor: surface.muted,
      hoverBackgroundColor: surface.base,
    },
    chipContent: {
      gap: space.smMinus,
    },
  },
  iconButton: {
    root: {
      borderRadius: radius.lg,
      padding: space.xs,
      border: border.medium,
    },
    // The color of every icon button, with neutral icons following it.
    // Without a color they keep MUI's and their icons' own colors.
    defaultColor: '',
    disabledColor: '',
    disabledOpacity: 0.3,
    // Draws the data health widgets' faded settings gear at full strength, so
    // it doesn't read as disabled.
    solidSettingsIcon: false,
  },
  widgetButton: {
    bg: surface.subtle,
    fg: content.primary,
    border: '',
    hoverBg: surface.shade,
    hoverFg: content.primary,
    pressedBg: '',
    disabledBg: surface.subtle,
    disabledFg: '',
    disabledIcon: '',
  },
  textInputWithButton: {
    action: {
      bg: 'none',
      fg: content.primary,
      border: border.strong,
      hoverBg: 'none',
      hoverFg: content.primary,
      hoverBorder: border.strong,
      disabledBg: surface.muted,
      disabledFg: content.placeholder,
      disabledBorder: border.strong,
    },
  },
  splitButton: {
    caretButton: {
      marginLeft: space.hairline,
      outlinedBorderLeft: border.control,
      containedBorderLeft: border.controlStrong,
    },
    root: {
      fontSize: fontSize.xxl,
    },
  },
  tabs: {
    indicator: surface.inverse,
    verticalHoverRadius: `0 ${radius.md} ${radius.md} 0`,
    // Horizontal tabs. A look without a gap, hover radius, hover underline or
    // indicator inset keeps today's full-width tabs.
    fontSize: fontSize.base,
    padding: space.lg,
    gap: '',
    hoverBackground: surface.muted,
    hoverRadius: '',
    hoverUnderline: '',
    hoverGap: '',
    // The selected tab's hover background in a look with a hover underline.
    selectedHoverBackground: '',
    // Without a width tabs keep MUI's minimum.
    minWidth: '',
    indicatorInset: '',
  },
  toggleButton: {
    root: {
      paddingTop: space.smMinus,
      paddingBottom: space.smMinus,
      color: content.secondary,
      borderColor: border.controlStrong,
      borderRadius: radius.lg,
      // Rest and hover fills a look leaves empty keep MUI's.
      background: '',
      neutralHoverBackground: '',
      selectedBackground: surface.sunken,
      selectedHoverBackground: '',
      selectedColor: content.secondary,
      hoverColor: status.error.bright,
      hoverBackground: status.error.bgStrong,
      warningHoverColor: status.warning.main,
      warningHoverBackground: status.warning.bgStrong,
    },
  },
  segmentedButton: {
    toggleIconButton: {
      gap: space.sm,
      color: content.secondary,
      borderRadius: radius.lg,
      smallPadding: space.xs,
      mediumPadding: space.sm,
      largePadding: space.md,
      hoverBorder: status.error.bright,
      selectedColor: content.secondary,
      selectedBackground: 'unset',
      selectedBorder: status.error.bright,
      selectedShadow: `0 1px 3px 0 ${shadowColor.solid}, 0 4px 8px 3px ${shadowColor.solidSoft}`,
      selectedShadowSmall: `0 1px 2px 0 ${shadowColor.solid}, 0 2px 4px 2px ${shadowColor.solidSoft}`,
      selectedShadowMedium: `0 1px 3px 0 ${shadowColor.solid}, 0 3px 6px 2px ${shadowColor.solidSoft}`,
    },
    toggleButtonGroup: {
      gap: space.sm,
      groupedBorder: border.controlStrong,
      groupedBorderRadius: radius.lg,
      // A selected choice tile keeps this border, in full color.
      selectedBorder: '',
    },
  },
  table: {
    // Line under the header; without one the first row's top border shows.
    headerDivider: '',
    columnActions: {
      gap: space.md,
    },
    headerCell: {
      paddingBottom: space.md,
      selectionPaddingBottom: space.xxs,
      fontSize: fontSize.md,
      fontWeight: fontWeight.regular,
    },
    tableCell: {
      borderTop: border.muted,
      paddingRight: space.sm,
      fontSize: fontSize.base,
      expandedContentPadding: space.lg,
      expandedContentMarginBottom: space.xs,
      headSelectionPaddingBottom: space.md,
    },
    sortArrow: {
      fill: icon.secondary,
      hoverFill: icon.primary,
      activeFill: accent.link,
      activeHoverFill: accent.selected,
    },
    clickableTableRow: {
      selectedBackground: surface.muted,
      selectedIndicator: '',
      stickyBackground: surface.base,
      hoverBackground: surface.subtle,
      blinkBackground: surface.divider,
    },
    expandButtonCell: {
      svgColor: content.secondary,
      svgMarginTop: space.mdPlus,
      svgMarginLeft: space.xl,
    },
    skeletonRow: {
      borderTop: border.muted,
      gap: space.massive,
    },
    skeletonCell: {
      borderRadius: radius.sm,
    },
    headerCellContent: {
      gap: space.lg,
    },
    resizer: {
      handleBackground: surface.track,
      handleRadius: radius.sm,
      handleMarginTop: space.xxs,
      handleHoverBackground: surface.trackStrong,
      handleActiveBackground: surface.trackStrong,
    },
    groupHeaderRow: {
      backgroundColor: surface.base,
      hoverBackgroundColor: surface.subtle,
    },
    groupHeaderCell: {
      borderTop: border.muted,
      borderBottom: border.muted,
      padding: `${space.md} ${space.lg}`,
      compactPadding: `${space.smMinus} ${space.lg}`,
      fontSize: fontSize.base,
    },
    groupLoadingCell: {
      padding: `${space.md} ${space.lg}`,
    },
    floatingStickyGroupRow: {
      backgroundColor: surface.muted,
      hoverBackgroundColor: surface.subtle,
    },
    groupHeaderLeft: {
      gap: space.mdMinus,
      marginLeft: space.xxs,
    },
    groupExpandIcon: {
      color: content.secondary,
    },
    groupTitle: {
      color: content.primary,
    },
    groupCount: {
      fontWeight: fontWeight.regular,
      color: content.tertiary,
      marginLeft: space.xs,
    },
    groupMetadata: {
      gap: space.lg,
    },
    groupedTable: {
      color: content.secondary,
    },
    wrapper: {
      borderRadius: radius.lg,
      border: border.muted,
      backgroundColor: surface.subtle,
      defaultPadding: '14px 30px',
    },
    root: {
      cellBorder: border.muted,
      cellPadding: `${space.mdMinus} ${space.xxl}`,
      cellFontWeight: fontWeight.regular,
      cellFontSize: fontSize.md,
      headerColor: content.secondary,
    },
    stickyCell: {
      background: surface.base,
      dividerColor: border.muted,
      dividerShadowColor: border.strong,
    },
    tableHead: {
      background: surface.base,
    },
  },
  switch: {
    root: {
      switchBaseMargin: space.xxs,
      thumbColor: content.inverse,
      switchBaseCheckedColor: content.inverse,
      checkedTrackBackgroundColor: surface.inverse,
      checkedTrackShadow: 'none',
      focusVisibleThumbColor: content.primary,
      disabledThumbColor: content.inverse,
      checkedDisabledThumbColor: content.inverse,
      trackBackgroundColor: surface.trackStrong,
      trackShadow: 'none',
      focusVisibleThumbBorder: border.inverse,
      trackBorderRadius: '13px',
    },
  },
  textField: {
    input: {
      fontSize: fontSize.base,
      radius: radius.lg,
      disabledBackground: surface.muted,
      disabledColor: content.placeholder,
      border: border.control,
      outlineRadius: radius.lg,
      focusedOutline: border.strong,
      hoverOutline: border.strong,
      focusedBorder: border.strong,
      labelShrinkFontSize: fontSize.base,
      placeholderColor: content.subtle,
      errorBorder: status.error.bright,
      errorFocusedOutline: border.strong,
      errorHoverOutline: border.strong,
      // Fills and texts a look leaves empty keep MUI's.
      errorBackground: '',
      errorFocusedBackground: '',
      hoverShadow: 'none',
      focusShadow: 'none',
      errorFocusShadow: 'none',
      disabledOutline: 'none',
      disabledTextFill: '',
      disabledPlaceholder: '',
      autofillShadow: `0 0 0 30px ${surface.base} inset`,
    },
  },
  dropDown: {
    chevron: {
      color: icon.translucent.active,
      disabledColor: icon.translucent.disabled,
    },
    select: {
      borderRadius: radius.lg,
      borderColor: border.strong,
      color: content.primary,
      fontSize: fontSize.base,
      border: border.control,
      focusedBorder: border.strong,
      hoverBorder: border.strong,
      errorBorder: status.error.bright,
      errorHoverBorder: status.error.bright,
      disabledBackground: surface.muted,
      disabledTextColor: content.disabled,
      valueFontSize: fontSize.base,
      disabledBorder: border.disabled,
      background: surface.base,
      hoverShadow: 'none',
      focusShadow: 'none',
      errorBackground: '',
      errorFocusedBackground: '',
      errorFocusShadow: 'none',
    },
    searchFieldContainer: {
      borderBottom: border.light,
      background: surface.base,
    },
    searchIconFocused: {
      pathFill: icon.primary,
    },
    divider: {
      background: surface.hover,
      marginBottom: space.sm,
    },
    menuPaper: {
      borderRadius: radius.lg,
      borderColor: border.light,
    },
    root: {
      color: content.tertiary,
      gap: space.sm,
      fontSize: fontSize.sm,
    },
    counter: {
      fontSize: fontSize.sm,
      color: content.secondary,
    },
    options: {
      fontSize: fontSize.sm,
      color: content.tertiary,
    },
    menuItem: {
      padding: space.sm,
      paddingLg: space.lg,
      borderRadius: radius.lg,
      fontSize: fontSize.base,
      hoverBackground: surface.tinted,
      selectedBackground: 'none',
      selectedHoverBackground: surface.tinted,
    },
    label: {
      shrinkFontSize: fontSize.base,
      shrinkPadding: `0 ${space.xs}`,
      background: surface.base,
    },
  },
  filterChip: {
    grayText: {
      color: content.muted,
      fontSize: fontSize.md,
    },
    wrapper: {
      backgroundColor: surface.tinted,
      border: border.selected,
      borderRadius: radius.lg,
      padding: `0 ${space.md}`,
      deletablePadding: `0 ${space.xs} 0 ${space.md}`,
      gap: space.lg,
    },
    closeButtonWrapper: {
      borderLeft: border.selected,
      paddingLeft: space.xs,
    },
    iconButton: {
      padding: space.smMinus,
    },
    filterValueText: {
      fontSize: fontSize.md,
    },
    flexGap: {
      gap: space.xs,
    },
    tooltipContentWrapper: {
      padding: space.xs,
    },
    filterValueSection: {
      gap: space.xs,
    },
    root: {
      fontSize: fontSize.md,
    },
    searchFieldContainer: {
      borderBottom: border.light,
      background: surface.base,
    },
    searchIconFocused: {
      pathFill: icon.primary,
    },
    selectedSectionHeader: {
      fontSize: fontSize.sm,
      color: content.tertiary,
      padding: `${space.sm} ${space.md} ${space.xs}`,
    },
    showMoreButton: {
      fontSize: fontSize.sm,
      color: accent.primary,
      padding: `${space.sm} ${space.md}`,
    },
    divider: {
      background: surface.hover,
      margin: `${space.sm} 0`,
    },
    noOptionsText: {
      fontSize: fontSize.sm,
      padding: space.sm,
    },
    menuItemWrapper: {
      paddingLeft: space.xs,
      paddingRight: space.xs,
    },
    optionContentContainer: {
      gap: space.sm,
      paddingRight: space.sm,
    },
    counterText: {
      fontSize: fontSize.sm,
      color: content.secondary,
    },
    optionItem: {
      fontSize: fontSize.sm,
      color: content.tertiary,
    },
    selectAllMenuItem: {
      marginTop: space.xs,
      marginBottom: space.sm,
      gap: space.sm,
    },
    selectAllText: {
      paddingRight: space.sm,
    },
  },
  // The All / Asset runs / Tests filters above a list of monitors; without
  // hover colors a hovered filter only fades.
  filterPill: {
    radius: radius.xxl,
    border: `1px solid ${border.emphasis}`,
    bg: surface.subtle,
    fg: content.primary,
    activeBorder: 'none',
    activeBg: surface.inverse,
    activeFg: content.inverse,
    hoverOpacity: 0.8,
    hoverBg: '',
    hoverBorder: '',
    hoverFg: '',
  },
  snackbar: {
    radius: radius.sm,
    // Toasts without a variant are drawn as info toasts.
    infoByDefault: false,
    // Toast surfaces by variant; without a background a toast keeps MUI's
    // dark snackbar.
    toast: {
      info: { background: '', border: '', text: '', shadow: '' },
      success: { background: '', border: '', text: '', shadow: '' },
      warning: { background: '', border: '', text: '', shadow: '' },
      error: { background: '', border: '', text: '', shadow: '' },
    },
    backdropFilter: 'none',
    message: {
      successColor: status.success.main,
      warningColor: status.warning.strong,
      errorColor: status.error.soft,
      infoColor: accent.primary,
    },
    actionButton: {
      color: content.inverse,
    },
  },
});

export type ComponentTokens = ReturnType<typeof buildComponentTokens>;

export const componentTokens = buildComponentTokens(colorTokens, scaleTokens);
